import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { getFieldInputId, mergeOptions, readApiPayload } from './utils.js';
import { useDropdownDismiss, useAssociationSearch, useInfiniteScrollLoader } from './hooks.js';
import { DatePicker } from './DatePicker.jsx';

export function MessageList({ messages }) {
  if (!messages.length) {
    return null;
  }

  return (
    <div className="cat-react-message-stack" aria-live="polite">
      {messages.map((message) => (
        <div
          key={message.id}
          className={`cat-react-banner cat-react-banner--${message.type === 'error' ? 'error' : 'status'}`}
          role={message.type === 'error' ? 'alert' : 'status'}
        >
          {message.text}
        </div>
      ))}
    </div>
  );
}

export function FieldWrapper({ field, error, inputId, children, hint }) {
  if (field.component === 'inline_template') {
    return (
      <div className="cat-react-inline-template" dangerouslySetInnerHTML={{ __html: field.html || '' }} />
    );
  }

  return (
    <div className={`cat-react-field${error ? ' cat-react-field--invalid' : ''}`} data-field-name={field.name}>
      <label className="cat-react-field__label" htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="cat-react-field__required">*</span> : null}
      </label>
      {field.description ? <span className="cat-react-field__description">{field.description}</span> : null}
      {children}
      {hint}
      {error ? <span className="cat-react-field__error">{error}</span> : null}
    </div>
  );
}

export function EntitySearchInput({ field, value, error, disabled, onChange }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState(field.options || []);
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const selectId = getFieldInputId(field.name);

  useEffect(() => {
    setOptions((previous) => mergeOptions(previous, field.options || []));
  }, [field.options]);

  useEffect(() => {
    if (!field.searchUrl || query.trim().length < 2) {
      setLookupError('');
      return undefined;
    }

    let isActive = true;
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      setLookupError('');

      try {
        const url = new URL(field.searchUrl, window.location.origin);
        url.searchParams.set('q', query.trim());

        const response = await window.fetch(url.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        });

        const data = await readApiPayload(response);
        if (!response.ok || data.ok === false) {
          throw new Error(data.message || 'Unable to load matching options.');
        }

        if (isActive) {
          setOptions((previous) => mergeOptions(previous, data.items || []));
        }
      } catch (fetchError) {
        if (isActive) {
          setLookupError(fetchError.message || 'Unable to load matching options.');
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [field.searchUrl, query]);

  return (
    <FieldWrapper
      field={field}
      error={error}
      inputId={selectId}
      hint={lookupError ? <span className="cat-react-field__hint cat-react-field__hint--error">{lookupError}</span> : null}
    >
      <div className="cat-react-entity-search">
        <input
          id={`${selectId}-search`}
          name={`${field.name}__search`}
          className="cat-react-input"
          type="search"
          placeholder="Search..."
          value={query}
          disabled={disabled}
          onChange={(event) => setQuery(event.target.value)}
        />
        <select
          id={selectId}
          name={field.name}
          className="cat-react-input"
          value={value ?? ''}
          disabled={disabled}
          aria-invalid={error ? 'true' : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
        >
          <option value="">{loading ? 'Loading...' : 'Select an item'}</option>
          {options.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </FieldWrapper>
  );
}

const ajaxAssociationSelectionCache = new Map();

export function rememberAjaxAssociationSelection(fieldName, item) {
  if (!fieldName || !item?.id) {
    return;
  }

  ajaxAssociationSelectionCache.set(`${fieldName}:${String(item.id)}`, {
    id: String(item.id),
    text: item.text || String(item.id),
  });
}

export function resolveAjaxAssociationSelection(field, value, fallbackSelected = null) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const stringValue = String(value);
  const matchingOption = (field.options || []).find((option) => String(option.value) === stringValue);
  if (matchingOption) {
    return {
      id: stringValue,
      text: matchingOption.label,
    };
  }

  if (fallbackSelected && String(fallbackSelected.id) === stringValue) {
    return fallbackSelected;
  }

  const cachedSelection = ajaxAssociationSelectionCache.get(`${field.name}:${stringValue}`);
  if (cachedSelection) {
    return cachedSelection;
  }

  return {
    id: stringValue,
    text: stringValue,
  };
}

export function AjaxAssociationSelectInput({ field, value, error, disabled, onChange, parentValue }) {
  const inputId = getFieldInputId(field.name);
  const config = useMemo(() => ({
    manager: field.ajax?.manager || '',
    method: field.ajax?.method || 'findBy',
    pageSize: Number.parseInt(field.ajax?.pageSize || '50', 10),
    initialPageSize: Number.parseInt(field.ajax?.initialPageSize || '20', 10),
    minimumInputLength: Number.parseInt(field.ajax?.minimumInputLength || '0', 10),
    extra: field.ajax?.extra || '',
    exclude: field.ajax?.exclude || '',
    placeholder: field.ajax?.placeholder || '-- Sélectionner --',
    baseUrl: window.drupalSettings?.path?.baseUrl ?? '/',
  }), [field.ajax]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => resolveAjaxAssociationSelection(field, value));
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const resultsSentinelRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();

  const isFirstParentRender = useRef(true);
  useEffect(() => {
    if (isFirstParentRender.current) {
      isFirstParentRender.current = false;
      return;
    }
    setSelected(null);
    onChange(field.name, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentValue]);
  const { loading, results, setResults, hasMore, loadMore } = useAssociationSearch({
    config,
    isOpen,
    search,
    allowEmptySearch: config.initialPageSize > 0,
    minimumInputLength: Math.max(config.minimumInputLength, 0),
    initialPageSize: config.initialPageSize,
  });

  useEffect(() => {
    if (selected?.id) {
      rememberAjaxAssociationSelection(field.name, selected);
    }
  }, [field.name, selected]);

  useEffect(() => {
    setSelected((previous) => resolveAjaxAssociationSelection(field, value, previous));
  }, [field.name, field.options, value]);

  useDropdownDismiss(containerRef, () => {
    setIsOpen(false);
    setSearch('');
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useInfiniteScrollLoader({
    containerRef: resultsRef,
    sentinelRef: resultsSentinelRef,
    onLoadMore: loadMore,
    enabled: isOpen && hasMore,
  });

  const open = () => {
    if (disabled) {
      return;
    }

    setIsOpen(true);
    setSearch('');
    setResults([]);
  };

  const pick = (item) => {
    const nextSelected = {
      id: String(item.id),
      text: item.text,
    };
    rememberAjaxAssociationSelection(field.name, nextSelected);
    setSelected(nextSelected);
    onChange(field.name, nextSelected.id);
    setIsOpen(false);
    setSearch('');
    setResults([]);
  };

  const minimumInputLength = Math.max(config.minimumInputLength, 1);
  const hasValue = Boolean(selected?.id);
  const trimmedSearch = search.trim();
  const isSearchTooShort = !loading && results.length === 0 && trimmedSearch.length > 0 && trimmedSearch.length < minimumInputLength;

  return (
    <FieldWrapper field={field} error={error} inputId={inputId}>
      <div className={`ras-wrap${isOpen ? ' ras-wrap--open' : ''}`} ref={containerRef}>
        <button
          id={inputId}
          name={field.name}
          type="button"
          className={`ras-value${hasValue ? '' : ' ras-value--placeholder'}`}
          onClick={open}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-invalid={error ? 'true' : undefined}
          disabled={disabled}
        >
          {hasValue ? selected.text : config.placeholder}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="ras-dropdown"
              role="listbox"
              initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scaleY: 0.96 }}
              animate={{ opacity: 1, y: 0, scaleY: 1 }}
              exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -4, scaleY: 0.96 }}
              transition={shouldReduceMotion ? { duration: 0 } : { duration: 0.15, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: 'top' }}
            >
              <div className="ras-search">
                <input
                  ref={inputRef}
                  type="text"
                  className="ras-search__input"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Rechercher…"
                  aria-autocomplete="list"
                />
              </div>
              <div className="ras-results" ref={resultsRef}>
                {results.length === 0 && loading && <div className="ras-msg">Recherche…</div>}
                {!loading && isSearchTooShort && (
                  <div className="ras-msg">
                    {`Saisir au moins ${minimumInputLength} caractère${minimumInputLength > 1 ? 's' : ''}…`}
                  </div>
                )}
                {!loading && !isSearchTooShort && results.length === 0 && <div className="ras-msg">Aucun résultat</div>}
                {results.map((item) => (
                  <div
                    key={item.id}
                    className={`ras-option${selected?.id == item.id ? ' ras-option--selected' : ''}`}
                    role="option"
                    aria-selected={selected?.id == item.id}
                    onMouseDown={() => pick(item)}
                  >
                    {item.text}
                  </div>
                ))}
                {hasMore && <div ref={resultsSentinelRef} className="ras-load-sentinel" aria-hidden="true" />}
                {results.length > 0 && loading && <div className="ras-msg ras-msg--load-more">Chargement…</div>}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </FieldWrapper>
  );
}

export function resolveVariant(field, values) {
  const keys = Object.keys(field.variants || {});
  if (keys.length === 0) {
    return null;
  }

  const currentValue = values[field.dependsOn];
  const selectedKey = Object.prototype.hasOwnProperty.call(field.variants || {}, String(currentValue))
    ? String(currentValue)
    : keys[0];

  return field.variants[selectedKey] ?? null;
}

export function FieldRenderer({ field, values, errors, disabled, onChange }) {
  const fieldDisabled = disabled || field.disabled;
  const value = values[field.name];
  const error = errors[field.name];
  const inputId = getFieldInputId(field.name);

  if (field.component === 'dependant') {
    const variantField = resolveVariant(field, values);
    return variantField ? (
      <div className="cat-react-dependant">
        <FieldRenderer field={variantField} values={values} errors={errors} disabled={fieldDisabled} onChange={onChange} />
      </div>
    ) : null;
  }

  if (field.component === 'dependant_group') {
    const variantFields = resolveVariant(field, values) || [];
    return (
      <div className="cat-react-grid">
        {variantFields.map((variantField) => (
          <FieldRenderer
            key={variantField.name}
            field={variantField}
            values={values}
            errors={errors}
            disabled={fieldDisabled}
            onChange={onChange}
          />
        ))}
      </div>
    );
  }

  if (field.component === 'unsupported') {
    return (
      <div className="cat-react-unsupported">
        <strong>{field.label}</strong>
        <span>{field.className} is not supported yet by the React renderer.</span>
      </div>
    );
  }

  if (field.component === 'entity_search') {
    return (
      <EntitySearchInput
        field={field}
        value={value}
        error={error}
        disabled={fieldDisabled}
        onChange={onChange}
      />
    );
  }

  if (field.component === 'textarea') {
    return (
      <FieldWrapper field={field} error={error} inputId={inputId}>
        <textarea
          id={inputId}
          name={field.name}
          className="cat-react-input cat-react-input--textarea"
          value={value ?? ''}
          maxLength={field.maxLength || undefined}
          disabled={fieldDisabled}
          aria-invalid={error ? 'true' : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
        />
      </FieldWrapper>
    );
  }

  if (field.component === 'checkbox') {
    return (
      <FieldWrapper field={field} error={error} inputId={inputId}>
        <label className="cat-react-toggle">
          <input
            id={inputId}
            name={field.name}
            className="cat-react-toggle__input"
            type="checkbox"
            checked={Boolean(value)}
            disabled={fieldDisabled}
            aria-invalid={error ? 'true' : undefined}
            onChange={(event) => onChange(field.name, event.target.checked)}
          />
          <span className="cat-react-toggle__track">
            <span className="cat-react-toggle__thumb" />
          </span>
        </label>
      </FieldWrapper>
    );
  }

  if (field.component === 'ajax_select') {
    const parentValue = field.dependsOn ? values[field.dependsOn] : undefined;
    let effectiveField = field;

    if (field.dependsOn && field.dependsOnParam) {
      const existingExtra = field.ajax?.extra ? JSON.parse(field.ajax.extra) : {};
      if (parentValue) {
        existingExtra[field.dependsOnParam] = parseInt(parentValue, 10);
      } else {
        delete existingExtra[field.dependsOnParam];
      }
      const extraStr = Object.keys(existingExtra).length > 0 ? JSON.stringify(existingExtra) : '';
      effectiveField = {
        ...field,
        ajax: {
          ...field.ajax,
          extra: extraStr,
        },
      };
    }

    return (
      <AjaxAssociationSelectInput
        field={effectiveField}
        value={value}
        error={error}
        disabled={fieldDisabled}
        onChange={onChange}
        parentValue={parentValue}
      />
    );
  }

  if (field.component === 'select') {
    return (
      <FieldWrapper field={field} error={error} inputId={inputId}>
        <select
          id={inputId}
          name={field.name}
          className="cat-react-input"
          value={value ?? ''}
          disabled={fieldDisabled}
          aria-invalid={error ? 'true' : undefined}
          onChange={(event) => onChange(field.name, event.target.value)}
        >
          {field.placeholder ? (
            <option value="" disabled>{field.placeholder}</option>
          ) : null}
          {(field.options || []).map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldWrapper>
    );
  }

  if (field.component === 'multiselect') {
    const selectedValues = Array.isArray(value) ? value.map(String) : [];

    return (
      <FieldWrapper field={field} error={error} inputId={inputId}>
        <select
          id={inputId}
          name={field.name}
          className="cat-react-input cat-react-input--multiselect"
          value={selectedValues}
          multiple
          disabled={fieldDisabled}
          aria-invalid={error ? 'true' : undefined}
          onChange={(event) => {
            const nextValues = Array.from(event.target.selectedOptions).map((option) => option.value);
            onChange(field.name, nextValues);
          }}
        >
          {(field.options || []).map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldWrapper>
    );
  }

  if (field.component === 'date') {
    return (
      <FieldWrapper field={field} error={error} inputId={inputId}>
        <DatePicker
          id={inputId}
          name={field.name}
          value={value ?? ''}
          disabled={fieldDisabled}
          ariaInvalid={error ? 'true' : undefined}
          onChange={(val) => onChange(field.name, val)}
        />
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper field={field} error={error} inputId={inputId}>
      <input
        id={inputId}
        name={field.name}
        className="cat-react-input"
        type={
          field.component === 'number'
            ? 'number'
            : field.component === 'email'
              ? 'email'
              : field.component === 'datetime'
                ? 'datetime-local'
                : field.component === 'color'
                  ? 'color'
                  : 'text'
        }
        value={value ?? ''}
        min={field.min ?? undefined}
        max={field.max ?? undefined}
        step={field.step ?? undefined}
        maxLength={field.maxLength || undefined}
        disabled={fieldDisabled}
        aria-invalid={error ? 'true' : undefined}
        onChange={(event) => onChange(field.name, event.target.value)}
      />
    </FieldWrapper>
  );
}
